/**
 * ExperimentClient — Banner A/B experiment runtime.
 *
 * Fetches the running experiment for a domain, assigns a visitor to a variant
 * deterministically (sticky across visits), and records impressions and
 * outcomes to the daily aggregate via the experiment-event edge function.
 */

export type ExperimentVariant = "a" | "b";
export type ExperimentEvent = "impression" | "accept" | "reject" | "custom_accept";

export interface ActiveExperiment {
  id: string;
  variant_a: Record<string, unknown>;
  variant_b: Record<string, unknown>;
  traffic_split: number;
}

export class ExperimentClient {
  constructor(
    private apiUrl: string,
    private anonKey: string,
    private domainId: string
  ) {}

  async fetchActive(): Promise<ActiveExperiment | null> {
    try {
      const res = await fetch(
        `${this.apiUrl}/functions/v1/banner-experiment?domain_id=${this.domainId}`,
        {
          headers: { apikey: this.anonKey, Authorization: `Bearer ${this.anonKey}` },
          signal: AbortSignal.timeout(3000),
        }
      );
      if (!res.ok) return null;
      const body = (await res.json()) as { experiment?: ActiveExperiment | null };
      const exp = body.experiment;
      if (!exp || !exp.id) return null;
      return exp;
    } catch {
      return null;
    }
  }

  record(experimentId: string, variant: ExperimentVariant, event: ExperimentEvent): void {
    try {
      void fetch(`${this.apiUrl}/functions/v1/experiment-event`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: this.anonKey,
          Authorization: `Bearer ${this.anonKey}`,
        },
        body: JSON.stringify({ experimentId, variant, event }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      // best effort
    }
  }

  /**
   * Deterministic sticky assignment: the same visitor and experiment always
   * map to the same variant, so a returning visitor sees a stable banner.
   */
  static assignVariant(visitorId: string, experimentId: string, trafficSplit: number): ExperimentVariant {
    const split = Math.min(99, Math.max(1, trafficSplit || 50));
    return ExperimentClient.hash(`${visitorId}:${experimentId}`) % 100 < split ? "a" : "b";
  }

  private static hash(str: string): number {
    // FNV-1a, 32-bit.
    let h = 0x811c9dc5;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
  }
}
